import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import helpdeskService, {
  PhotoUpload,
  technicianAtom,
  TicketResponse,
  RepairCostResponse,
  InternalCommentResponse,
  MessageResponse,
  TicketFilters
} from './helpdesk.service'
import { useAtom } from 'jotai'
import { uploadProgressStore } from '@services/uploadProgress/uploadProgress.store'

// Query keys for React Query
export const HELPDESK_QUERY_KEYS = {
  tickets: (filters?: TicketFilters) => ['tickets', filters],
  ticket: (id: number) => ['ticket', id],
  repairCosts: (ticketId: number) => ['repairCosts', ticketId],
  internalComments: (ticketId: number) => ['internalComments', ticketId],
  messages: (ticketId: number) => ['messages', ticketId],
  technician: 'technician'
}

// Hook for fetching tickets list
export const useTickets = (filters?: TicketFilters) => {
  return useQuery<TicketResponse>({
    queryKey: HELPDESK_QUERY_KEYS.tickets(filters),
    queryFn: async () => {
      const response = await helpdeskService.getTickets(filters)
      return response.data
    },
    enabled: true
  })
}

// Hook for fetching tickets list with infinite pagination
export const useInfiniteTickets = (filters?: Omit<TicketFilters, 'page'>) => {
  return useInfiniteQuery<TicketResponse>({
    queryKey: HELPDESK_QUERY_KEYS.tickets(filters),
    queryFn: async ({ pageParam }) => {
      const response = await helpdeskService.getTickets({
        ...filters,
        page: pageParam as number
      })
      return response.data
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined)
  })
}

// Hook for fetching ticket details
export const useTicket = (id: number) => {
  return useQuery({
    queryKey: HELPDESK_QUERY_KEYS.ticket(id),
    queryFn: async () => {
      const response = await helpdeskService.getTicket(id)
      return response.data
    },
    enabled: !!id
  })
}

// Hook for fetching repair costs for a ticket
export const useRepairCosts = (ticketId: number) => {
  return useQuery<RepairCostResponse>({
    queryKey: HELPDESK_QUERY_KEYS.repairCosts(ticketId),
    queryFn: async () => {
      const response = await helpdeskService.getRepairCosts(ticketId)
      return response.data
    },
    enabled: !!ticketId
  })
}

// Hook for assigning repair cost to technician
export const useAssignRepairCost = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ ticketId, repairCostId }: { ticketId: number; repairCostId: number }) => {
      const response = await helpdeskService.assignRepairCost(ticketId, repairCostId)
      return response.data
    },
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: HELPDESK_QUERY_KEYS.repairCosts(ticketId) })
    }
  })
}

// Hook for marking repair cost as done
export const useMarkRepairCostAsDone = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ ticketId, repairCostId }: { ticketId: number; repairCostId: number }) => {
      const response = await helpdeskService.markRepairCostAsDone(ticketId, repairCostId)
      return response.data
    },
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: HELPDESK_QUERY_KEYS.repairCosts(ticketId) })
    }
  })
}

// Hook for fetching internal comments for a ticket
export const useInternalComments = (ticketId: number) => {
  return useQuery<InternalCommentResponse>({
    queryKey: HELPDESK_QUERY_KEYS.internalComments(ticketId),
    queryFn: async () => {
      const response = await helpdeskService.getInternalComments(ticketId)
      return response.data
    },
    enabled: !!ticketId
  })
}

// Hook for adding an internal comment to a ticket
export const useAddInternalComment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ticketId,
      comment,
      photoUploads
    }: {
      ticketId: number
      comment: string
      photoUploads?: PhotoUpload[]
    }) => {
      try {
        const response = await helpdeskService.addInternalComment(ticketId, comment, photoUploads)
        return response.data
      } catch (error) {
        console.error('useAddInternalComment - Error:', error)
        throw error
      }
    },
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: HELPDESK_QUERY_KEYS.internalComments(ticketId) })
    },
    onError: (error) => {
      console.error('useAddInternalComment - Error in mutation:', error)
    },
    onSettled: () => {
      uploadProgressStore.clear()
    }
  })
}

// Hook for fetching messages for a ticket
export const useMessages = (ticketId: number) => {
  return useQuery<MessageResponse>({
    queryKey: HELPDESK_QUERY_KEYS.messages(ticketId),
    queryFn: async () => {
      const response = await helpdeskService.getMessages(ticketId)
      return response.data
    },
    enabled: !!ticketId
  })
}

// Hook for adding a message to a ticket
export const useAddMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ticketId,
      message,
      attachment
    }: {
      ticketId: number
      message: string
      attachment?: File
    }) => {
      try {
        const response = await helpdeskService.addMessage(ticketId, message, attachment)
        return response.data
      } catch (error) {
        console.error('useAddMessage - Error:', error)
        throw error
      }
    },
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: HELPDESK_QUERY_KEYS.messages(ticketId) })
    },
    onError: (error) => {
      console.error('useAddMessage - Error in mutation:', error)
    },
    onSettled: () => {
      uploadProgressStore.clear()
    }
  })
}

// Hook for getting technician information
export const useTechnician = () => {
  const [technicianId, setTechnicianId] = useAtom(technicianAtom)

  const query = useQuery({
    queryKey: [HELPDESK_QUERY_KEYS.technician],
    queryFn: async () => {
      const response = await helpdeskService.getTechnician()
      return response.data
    },
    enabled: true,
    staleTime: Infinity
  })

  // Save technician ID to atom after fetching data
  if (query.data && !technicianId) {
    setTechnicianId(query.data.id)
  }

  return query
}

// Hook for updating ticket status
export const useUpdateTicketStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ ticketId, status }: { ticketId: number; status: string }) => {
      const response = await helpdeskService.updateTicketStatus(ticketId, status)
      return response.data
    },
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: HELPDESK_QUERY_KEYS.ticket(ticketId) })
    }
  })
}
